/**
 * Where a new account came from: the UTM tags, the other site, the page it
 * landed on — carried to the first `/session`, which writes them to the
 * account once and never again.
 *
 * The visit that brought somebody here is rarely the one they sign up on, and
 * a Google redirect or an e-mail link reloads the page in between. So the
 * first visit worth remembering is kept in `localStorage` — but only with the
 * statistics consent, because a note of where somebody came from, tied to
 * their account, is statistics and nothing else. Without it the visit is held
 * in memory for this page view and sent only if the consent is given before
 * signing in; declined, nothing is stored and nothing is sent.
 *
 * First touch wins: a later visit with tags of its own does not overwrite an
 * earlier one, and a visit with neither tags nor another site behind it
 * (typed in, a bookmark) is not a source at all.
 */
import { getConsent, onConsentChange } from "@/state/cookie-consent";
import type { SignupSource } from "@shared/signup-source";

const KEY = "telc.source";

const UTM = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  utm_term: "utmTerm",
} as const;

/** Hosts a sign-in or a payment sends people back from — a step, not a source. */
const PASS_THROUGH = /(^|\.)(firebaseapp\.com|accounts\.google\.com|stripe\.com)$/;

function fromThisVisit(): SignupSource | null {
  const url = new URL(window.location.href);
  const found: Partial<SignupSource> = {};
  for (const [param, field] of Object.entries(UTM)) {
    const value = url.searchParams.get(param)?.trim().slice(0, 200);
    if (value) found[field] = value;
  }
  let referrer: string | undefined;
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : "";
    if (host && host !== url.hostname && !PASS_THROUGH.test(host)) referrer = host;
  } catch {
    /* an unparseable referrer is no referrer */
  }
  if (referrer) found.referrerHost = referrer;
  if (Object.keys(found).length === 0) return null;
  return { ...found, landingPath: url.pathname.slice(0, 200), at: Date.now() };
}

const allowed = (): boolean => getConsent()?.analytics === "accepted";

function stored(): SignupSource | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SignupSource) : null;
  } catch {
    return null;
  }
}

function store(source: SignupSource): void {
  try {
    if (!localStorage.getItem(KEY)) localStorage.setItem(KEY, JSON.stringify(source));
  } catch {
    /* private mode: the visit is lost, and the account simply has no source */
  }
}

// Read once, when the app loads — the address the visitor arrived on.
const visit = typeof window === "undefined" ? null : fromThisVisit();
if (visit && allowed()) store(visit);
if (typeof window !== "undefined") {
  onConsentChange((state) => {
    if (state?.analytics === "accepted" && visit) store(visit);
    // Withdrawn: whatever was kept goes with it.
    if (state?.analytics === "declined") forgetSignupSource();
  });
}

/** What to send with `/session`, or null — never without the statistics consent. */
export function pendingSignupSource(): SignupSource | null {
  if (!allowed()) return null;
  return stored() ?? visit;
}

/** Delivered, or no longer wanted. */
export function forgetSignupSource(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing was kept */
  }
}
